<template>
    <ModalContainer @click.stop>
        <!-- ! 헤더 -->
        <ModalHeader>
            <ModalTitle>
                <CalculatorIcon>🧮</CalculatorIcon>
                청구 계산
            </ModalTitle>
        </ModalHeader>

        <!-- ! 탭 네비게이션 -->
        <TabContainer>
            <TabButton :class="{ active: activeTab === 'diabetes' }" @click="setActiveTab('diabetes')">
                <Diabetes :size="24" :style="{ paddingTop: '3px' }" />
                당뇨소모성재료
            </TabButton>
            <TabButton :class="{ active: activeTab === 'oxygen' }" @click="setActiveTab('oxygen')">
                <Oxygen :size="24" :style="{ paddingTop: '3px' }" />
                산소발생기
            </TabButton>
            <TabButton :class="{ active: activeTab === 'assistance' }" @click="setActiveTab('assistance')">
                <Assistance :size="24" :style="{ paddingTop: '3px' }" />
                보조기기
            </TabButton>
        </TabContainer>

        <!-- ! 바디 -->
        <ModalBody>
            <!-- * 당뇨소모성재료 탭 -->
            <TabContent v-if="activeTab === 'diabetes'">
                <TwoColumnGrid>
                    <InfoSection>
                        <SectionTitleCompact>📋 처방 정보</SectionTitleCompact>

                        <FormGridCompact>
                            <FormGroupInline>
                                <FormLabelCompact>총 처방일수</FormLabelCompact>
                                <FormInputCompact type="text" :value="diabetesData.totalPeriodDays"
                                    @input="handleNumberInput('totalPeriodDays', $event.target.value)" />
                                <UnitLabel>일</UnitLabel>
                            </FormGroupInline>

                            <FormGroupInline>
                                <FormLabelCompact>혈당검사 횟수</FormLabelCompact>
                                <FormInputCompact type="text" :value="diabetesData.bloodTestCount"
                                    @input="handleNumberInput('bloodTestCount', $event.target.value)" />
                                <UnitLabel>회/일</UnitLabel>
                            </FormGroupInline>

                            <FormGroupInline>
                                <FormLabelCompact>인슐린주사 횟수</FormLabelCompact>
                                <FormInputCompact type="text" :value="diabetesData.insulinCount"
                                    @input="handleNumberInput('insulinCount', $event.target.value)" />
                                <UnitLabel>회/일</UnitLabel>
                            </FormGroupInline>

                            <!-- 기준금액/한도 미리보기 -->
                            <FormGroupInline v-if="diabetesSummary">
                                <FormLabelCompact>기준금액 요약</FormLabelCompact>
                                <UnitLabel>
                                    {{ formatPrice(diabetesSummary.dailyCap) }}원/일 ×
                                    {{ diabetesSummary.totalDays }}일 =
                                    <strong>{{ formatPrice(diabetesSummary.capTotal) }}원</strong>
                                </UnitLabel>
                            </FormGroupInline>
                        </FormGridCompact>
                    </InfoSection>

                    <CompactProductInfoSection>
                        <SectionTitleCompact>📦 선택된 품목</SectionTitleCompact>

                        <CompactProductList>
                            <CompactProductItem v-for="product in selectedProductsWithQuantity" :key="product.id">
                                <CompactProductHeader>
                                    <CompactProductName>{{ product.name }}</CompactProductName>
                                    <CompactProductType v-if="product.type === '박스'">
                                        <TypeBadge>📦 박스</TypeBadge>
                                    </CompactProductType>
                                </CompactProductHeader>

                                <CompactProductDetails>
                                    <CompactProductDetail>
                                        <CompactDetailLabel>가격:</CompactDetailLabel>
                                        <CompactDetailValue>{{ formatPrice(product.price || 0) }}원
                                            <span v-if="product.type === '박스'"></span>
                                            <span v-else>(개당)</span>
                                        </CompactDetailValue>
                                    </CompactProductDetail>

                                    <CompactProductDetail>
                                        <CompactDetailLabel>수량:</CompactDetailLabel>
                                        <CompactDetailValue>
                                            <QuantityControlInline>
                                                <QuantityButtonInline @click="decreaseQuantity(product.id)"
                                                    :disabled="product.currentQuantity <= 1">-</QuantityButtonInline>
                                                <QuantityInputInline :value="product.currentQuantity"
                                                    @input="updateQuantity(product.id, $event.target.value)" />
                                                <QuantityButtonInline @click="increaseQuantity(product.id)"
                                                    :disabled="product.currentQuantity >= 999">+</QuantityButtonInline>
                                            </QuantityControlInline>
                                        </CompactDetailValue>
                                    </CompactProductDetail>

                                    <CompactProductDetail v-if="product.type === '박스' && product.pieces_per_box">
                                        <CompactDetailLabel>박스당 개수:</CompactDetailLabel>
                                        <CompactDetailValue>{{ product.pieces_per_box }}개</CompactDetailValue>
                                    </CompactProductDetail>

                                    <CompactProductDetail>
                                        <CompactDetailLabel>총 제공수량:</CompactDetailLabel>
                                        <CompactDetailValue class="total-count">
                                            <template v-if="product.type === '박스'">
                                                {{ product.currentQuantity * (product.pieces_per_box || 1) }}개
                                            </template>
                                            <template v-else>
                                                {{ product.currentQuantity }}개
                                            </template>
                                        </CompactDetailValue>
                                    </CompactProductDetail>
                                </CompactProductDetails>
                            </CompactProductItem>

                            <EmptyProduct v-if="!selectedProducts || selectedProducts.length === 0">
                                <EmptyIcon>📋</EmptyIcon>
                                <EmptyText>선택된 품목이 없습니다</EmptyText>
                                <EmptySubText>청구 계산할 품목을 선택해주세요</EmptySubText>
                            </EmptyProduct>
                        </CompactProductList>
                    </CompactProductInfoSection>
                </TwoColumnGrid>

                <!-- ✅ 변경된 결과 테이블: 권장 헤더 & 새로운 청구 계산 적용 -->
                <ClaimResultTable v-if="diabetesSummary && selectedProducts.length > 0">
                    <SectionTitleCompact>💰 청구 계산 결과</SectionTitleCompact>

                    <TableContainer>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>품목</TableHeaderCell>
                                    <TableHeaderCell>수량/단가</TableHeaderCell>
                                    <TableHeaderCell>실구입가</TableHeaderCell>
                                    <TableHeaderCell>기준내 배정액</TableHeaderCell>
                                    <TableHeaderCell>공단 부담(90%)</TableHeaderCell>
                                    <TableHeaderCell>본인부담(10%)</TableHeaderCell>
                                    <TableHeaderCell>초과금액(100%)</TableHeaderCell>
                                    <TableHeaderCell>최종 본인부담</TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                <TableRow v-for="item in diabetesCalculatedItems" :key="item.name">
                                    <TableCell>{{ item.name }}</TableCell>
                                    <TableCell>{{ item.qtyDesc }}</TableCell>
                                    <TableCell>{{ formatPrice(item.itemTotal) }}원</TableCell>
                                    <TableCell>{{ formatPrice(item.inCapAllocated) }}원</TableCell>
                                    <TableCell>{{ formatPrice(item.insurer) }}원</TableCell>
                                    <TableCell>{{ formatPrice(item.patientInCap) }}원</TableCell>
                                    <TableCell>{{ formatPrice(item.overCap) }}원</TableCell>
                                    <TableCell>{{ formatPrice(item.patientTotal) }}원</TableCell>
                                </TableRow>
                                <TableRow class="total-row">
                                    <TableCell><strong>합계</strong></TableCell>
                                    <TableCell>-</TableCell>
                                    <TableCell><strong>{{ formatPrice(diabetesTotals.totalPurchase) }}원</strong>
                                    </TableCell>
                                    <TableCell><strong>{{ formatPrice(diabetesTotals.inCapTotal) }}원</strong>
                                    </TableCell>
                                    <TableCell><strong>{{ formatPrice(diabetesTotals.totalInsurer) }}원</strong>
                                    </TableCell>
                                    <TableCell><strong>{{ formatPrice(diabetesTotals.totalPatientInCap) }}원</strong>
                                    </TableCell>
                                    <TableCell><strong>{{ formatPrice(diabetesTotals.totalOverCap) }}원</strong>
                                    </TableCell>
                                    <TableCell><strong>{{ formatPrice(diabetesTotals.totalPatient) }}원</strong>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </ClaimResultTable>
            </TabContent>

            <!-- 산소발생기 탭 내용 -->
            <TabContent v-if="activeTab === 'oxygen'">
                <TwoColumnGrid>
                    <InfoSection>
                        <SectionTitleCompact>📋 산소치료 정보</SectionTitleCompact>

                        <FormGroupInline>
                            <FormLabelCompact>산소발생기 유형</FormLabelCompact>
                            <OxygenRadioGroup>
                                <OxygenRadioItem>
                                    <OxygenRadioInput type="radio" :checked="oxygenData.isHomeUnit === true"
                                        @change="handleOxygenTypeChange(true)" />
                                    <OxygenRadioLabel>가정용 (약 4,000원/일)</OxygenRadioLabel>
                                </OxygenRadioItem>
                                <OxygenRadioItem>
                                    <OxygenRadioInput type="radio" :checked="oxygenData.isHomeUnit === false"
                                        @change="handleOxygenTypeChange(false)" />
                                    <OxygenRadioLabel>휴대용 (약 6,667원/일)</OxygenRadioLabel>
                                </OxygenRadioItem>
                            </OxygenRadioGroup>
                        </FormGroupInline>

                        <FormGridCompact>
                            <FormGroupInline>
                                <FormLabelCompact>산소치료 진단일</FormLabelCompact>
                                <OxygenFormInput type="date" :value="oxygenData.diagnosisDate"
                                    @input="handleOxygenDateInput('diagnosisDate', $event.target.value)" />
                            </FormGroupInline>

                            <FormGroupInline>
                                <FormLabelCompact>급여 적용일</FormLabelCompact>
                                <OxygenFormInput type="date" :value="oxygenData.benefitStartDate"
                                    @input="handleOxygenDateInput('benefitStartDate', $event.target.value)" />
                            </FormGroupInline>

                            <FormGroupInline>
                                <FormLabelCompact>계산 종료일</FormLabelCompact>
                                <OxygenFormInput type="date" :value="oxygenData.currentDate"
                                    @input="handleOxygenDateInput('currentDate', $event.target.value)" />
                            </FormGroupInline>
                        </FormGridCompact>
                    </InfoSection>

                    <!-- 계산 결과 섹션 -->
                    <CompactProductInfoSection v-if="oxygenCalculationResult">
                        <SectionTitleCompact>💰 계산 결과</SectionTitleCompact>

                        <OxygenResultContainer>
                            <OxygenResultItem>
                                <OxygenResultLabel>산소발생기 유형:</OxygenResultLabel>
                                <OxygenResultValue>{{ oxygenCalculationResult.isHomeUnit ? '가정용' : '휴대용' }}
                                </OxygenResultValue>
                            </OxygenResultItem>

                            <OxygenResultItem>
                                <OxygenResultLabel>산소치료 진단일:</OxygenResultLabel>
                                <OxygenResultValue>{{ formatDate(oxygenCalculationResult.diagnosisDate) }}
                                </OxygenResultValue>
                            </OxygenResultItem>

                            <OxygenResultItem>
                                <OxygenResultLabel>급여 적용일:</OxygenResultLabel>
                                <OxygenResultValue>{{ formatDate(oxygenCalculationResult.benefitStartDate) }}
                                </OxygenResultValue>
                            </OxygenResultItem>

                            <OxygenResultItem>
                                <OxygenResultLabel>계산 종료일:</OxygenResultLabel>
                                <OxygenResultValue>{{ formatDate(oxygenCalculationResult.currentDate) }}
                                </OxygenResultValue>
                            </OxygenResultItem>

                            <OxygenResultDivider />

                            <OxygenResultItem>
                                <OxygenResultLabel>총 이용 일수:</OxygenResultLabel>
                                <OxygenResultValue>{{ oxygenCalculationResult.totalDays }}일</OxygenResultValue>
                            </OxygenResultItem>

                            <OxygenResultItem>
                                <OxygenResultLabel>비급여 일수:</OxygenResultLabel>
                                <OxygenResultValue class="non-insurance-days">{{
                                    oxygenCalculationResult.nonInsuranceDays }}일</OxygenResultValue>
                            </OxygenResultItem>

                            <OxygenResultItem>
                                <OxygenResultLabel>급여 일수:</OxygenResultLabel>
                                <OxygenResultValue class="insurance-days">{{ oxygenCalculationResult.insuranceDays }}일
                                </OxygenResultValue>
                            </OxygenResultItem>

                            <OxygenResultDivider />

                            <OxygenResultItem>
                                <OxygenResultLabel>비급여 요금:</OxygenResultLabel>
                                <OxygenResultValue class="non-insurance-amount">{{
                                    formatPrice(oxygenCalculationResult.nonInsuranceAmount) }}원</OxygenResultValue>
                            </OxygenResultItem>

                            <OxygenResultItem>
                                <OxygenResultLabel>급여 요금 (10% 부담):</OxygenResultLabel>
                                <OxygenResultValue class="insurance-amount">{{
                                    formatPrice(oxygenCalculationResult.insuranceAmount) }}원</OxygenResultValue>
                            </OxygenResultItem>

                            <OxygenResultItem class="total-row">
                                <OxygenResultLabel>총 부담금:</OxygenResultLabel>
                                <OxygenResultValue class="total-amount">{{
                                    formatPrice(oxygenCalculationResult.totalAmount) }}원</OxygenResultValue>
                            </OxygenResultItem>
                        </OxygenResultContainer>
                    </CompactProductInfoSection>
                </TwoColumnGrid>

                <!-- 상세 계산 내역 테이블 -->
                <ClaimResultTable v-if="oxygenCalculationResult && oxygenCalculationResult.detailBreakdown.length > 0">
                    <SectionTitleCompact>📊 상세 계산 내역</SectionTitleCompact>

                    <TableContainer>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>기간</TableHeaderCell>
                                    <TableHeaderCell>일수</TableHeaderCell>
                                    <TableHeaderCell>구분</TableHeaderCell>
                                    <TableHeaderCell>일 요금</TableHeaderCell>
                                    <TableHeaderCell>계산 금액</TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                <TableRow v-for="(item, index) in oxygenCalculationResult.detailBreakdown" :key="index">
                                    <TableCell>{{ formatDate(item.startDate) }} ~ {{ formatDate(item.endDate) }}
                                    </TableCell>
                                    <TableCell>{{ item.days }}일</TableCell>
                                    <TableCell>
                                        <OxygenTypeBadge :class="item.type === '급여' ? 'insurance' : 'non-insurance'">
                                            {{ item.type }}
                                        </OxygenTypeBadge>
                                    </TableCell>
                                    <TableCell>{{ formatPrice(item.dailyRate) }}원</TableCell>
                                    <TableCell>{{ formatPrice(item.amount) }}원</TableCell>
                                </TableRow>
                                <TableRow class="total-row">
                                    <TableCell><strong>합계</strong></TableCell>
                                    <TableCell><strong>{{ oxygenCalculationResult.totalDays }}일</strong></TableCell>
                                    <TableCell>-</TableCell>
                                    <TableCell>-</TableCell>
                                    <TableCell><strong>{{ formatPrice(oxygenCalculationResult.totalAmount) }}원</strong>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </ClaimResultTable>
            </TabContent>

            <!-- * 보조기기 탭 -->
            <TabContent v-if="activeTab === 'assistance'">
                <TwoColumnGrid>
                    <!-- 왼쪽 섹션: 입력 폼과 결과 -->
                    <InfoSection>
                        <SectionTitleCompact style="margin-bottom: 25px;">📋 보조기기 정보</SectionTitleCompact>

                        <FormGridCompact>
                            <FormGroupInline>
                                <FormLabelCompact>청구 타입</FormLabelCompact>
                                <AssistanceRadioGroup>
                                    <AssistanceRadioItem>
                                        <AssistanceRadioInput type="radio"
                                            :checked="assistanceData.claimType === 'general'"
                                            @change="assistanceData.claimType = 'general'" />
                                        <AssistanceRadioLabel>일반 (90%)</AssistanceRadioLabel>
                                    </AssistanceRadioItem>
                                    <AssistanceRadioItem>
                                        <AssistanceRadioInput type="radio"
                                            :checked="assistanceData.claimType === 'basic'"
                                            @change="assistanceData.claimType = 'basic'" />
                                        <AssistanceRadioLabel>기초/차상위 (100%)</AssistanceRadioLabel>
                                    </AssistanceRadioItem>
                                </AssistanceRadioGroup>
                            </FormGroupInline>

                            <FormGroupInline>
                                <FormLabelCompact>장애유형</FormLabelCompact>
                                <AssistanceSelect v-model="assistanceData.disabilityType"
                                    @change="onDisabilityTypeChange">
                                    <option value="">선택하세요</option>
                                    <option value="팔의지">팔의지</option>
                                    <option value="다리의지">다리의지</option>
                                    <option value="팔보조기">팔보조기</option>
                                    <option value="척추보조기">척추보조기</option>
                                    <option value="골반보조기">골반보조기</option>
                                    <option value="다리보조기">다리보조기</option>
                                    <option value="교정용 신발류">교정용 신발류</option>
                                    <option value="그 밖의 보조기기">그 밖의 보조기기</option>
                                </AssistanceSelect>
                            </FormGroupInline>

                            <FormGroupInline>
                                <FormLabelCompact>보조기기 유형</FormLabelCompact>
                                <AssistanceSelect v-model="assistanceData.type" @change="onAssistanceTypeChange">
                                    <option value="">선택하세요</option>
                                    <option v-for="item in filteredAssistanceItems" :key="item.id"
                                        :value="item.itemName">
                                        {{ item.itemName }}
                                    </option>
                                </AssistanceSelect>
                            </FormGroupInline>

                            <FormGroupInline>
                                <FormLabelCompact>기준액</FormLabelCompact>
                                <AssistanceFormInputCompact type="text"
                                    :value="formatNumber(assistanceData.standardAmount)"
                                    @input="handleAssistanceNumberInput('assistanceData.standardAmount', $event.target.value)" />
                                <UnitLabel>원</UnitLabel>
                            </FormGroupInline>

                            <FormGroupInline>
                                <FormLabelCompact>고시금액</FormLabelCompact>
                                <AssistanceFormInputCompact type="text"
                                    :value="formatNumber(assistanceData.announcedAmount || selectedProductPrice)"
                                    @input="handleAssistanceNumberInput('assistanceData.announcedAmount', $event.target.value)" />
                                <UnitLabel>원</UnitLabel>
                            </FormGroupInline>
                        </FormGridCompact>
                    </InfoSection>

                    <!-- 오른쪽 섹션: 공단 고시 내용 -->
                    <AssistanceGuideSection>
                        <SectionTitleCompact>📋 공단 고시 보조기기 목록</SectionTitleCompact>

                        <!-- 검색 입력 -->
                        <AssistanceSearchContainer>
                            <AssistanceSearchInput type="text" v-model="assistanceSearchKeyword"
                                placeholder="장애유형, 품목명으로 검색" @input="onAssistanceSearch" />
                        </AssistanceSearchContainer>

                        <AssistanceGuideContainer>
                            <AssistanceGuideTable>
                                <AssistanceGuideHeader>
                                    <AssistanceGuideRow>
                                        <AssistanceGuideCell>장애유형</AssistanceGuideCell>
                                        <AssistanceGuideCell>품목 및 기기명</AssistanceGuideCell>
                                        <AssistanceGuideCell>지원내용</AssistanceGuideCell>
                                    </AssistanceGuideRow>
                                </AssistanceGuideHeader>
                                <AssistanceGuideBody>
                                    <AssistanceGuideRow v-for="item in filteredAssistanceGuideData" :key="item.id"
                                        :class="{ 'highlighted': isItemHighlighted(item) }"
                                        @click="selectAssistanceItem(item)">
                                        <AssistanceGuideCell>{{ item.disabilityType }}</AssistanceGuideCell>
                                        <AssistanceGuideCell>{{ item.itemName }}</AssistanceGuideCell>
                                        <AssistanceGuideCell>{{ formatPrice(item.supportAmount) }}원
                                        </AssistanceGuideCell>
                                    </AssistanceGuideRow>
                                </AssistanceGuideBody>
                            </AssistanceGuideTable>

                            <!-- 검색 결과 없음 -->
                            <AssistanceNoResults v-if="filteredAssistanceGuideData.length === 0">
                                <AssistanceNoResultsIcon>🔍</AssistanceNoResultsIcon>
                                <AssistanceNoResultsText>검색 결과가 없습니다</AssistanceNoResultsText>
                                <AssistanceNoResultsSubText>"{{ assistanceSearchKeyword }}"에 대한 결과를 찾을 수 없습니다
                                </AssistanceNoResultsSubText>
                            </AssistanceNoResults>
                        </AssistanceGuideContainer>
                    </AssistanceGuideSection>
                </TwoColumnGrid>

                <!-- 상세 계산 내역 테이블 -->
                <ClaimResultTable v-if="assistanceCalculationResult">
                    <SectionTitleCompact>📊 상세 계산 내역</SectionTitleCompact>

                    <TableContainer>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>항목</TableHeaderCell>
                                    <TableHeaderCell>금액</TableHeaderCell>
                                    <TableHeaderCell>비고</TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                <TableRow>
                                    <TableCell>기준액 (공단 상한액)</TableCell>
                                    <TableCell>{{ formatPrice(assistanceCalculationResult.standardAmount) }}원
                                    </TableCell>
                                    <TableCell>보조기기별 공단 상한 금액</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>고시금액 (제품별 결정가격)</TableCell>
                                    <TableCell>{{ formatPrice(assistanceCalculationResult.announcedAmount) }}원
                                    </TableCell>
                                    <TableCell>제품별 복지부 고시 결정가격</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>실구입금액</TableCell>
                                    <TableCell>{{ formatPrice(assistanceCalculationResult.purchaseAmount) }}원
                                    </TableCell>
                                    <TableCell>실제 구입한 금액 (고시금액과 동일)</TableCell>
                                </TableRow>
                                <TableRow class="total-row">
                                    <TableCell><strong>본인부담금</strong></TableCell>
                                    <TableCell><strong>{{ formatPrice(assistanceCalculationResult.patientAmount)
                                    }}원</strong>
                                    </TableCell>
                                    <TableCell><strong>본인이 부담하는 금액</strong></TableCell>
                                </TableRow>
                                <TableRow class="total-row">
                                    <TableCell><strong>청구금액</strong></TableCell>
                                    <TableCell><strong>{{ formatPrice(assistanceCalculationResult.claimAmount)
                                    }}원</strong>
                                    </TableCell>
                                    <TableCell><strong>공단에 청구할 수 있는 금액</strong></TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </ClaimResultTable>
            </TabContent>
        </ModalBody>
    </ModalContainer>
</template>

<script>
import * as ClaimCalculationPopupCss from '@/assets/styles/order/popup/ClaimCalculationPopupCss.js';

import Diabetes from 'vue-material-design-icons/Diabetes.vue'
import Oxygen from 'vue-material-design-icons/GasCylinder.vue'
import Assistance from 'vue-material-design-icons/WheelchairAccessibility.vue'

export default {
    name: 'ClaimCalculationPopup',

    components: {
        ...ClaimCalculationPopupCss,
        Diabetes, Oxygen, Assistance
    },

    props: {
        show: {
            type: Boolean,
            default: false
        },
        selectedProducts: {
            type: Array,
            default: () => []
        },
        orderType: {
            type: String,
            default: 'diabetes'
        }
    },

    emits: ['close', 'confirm'],

    data() {
        return {
            activeTab: this.getInitialTab(),
            diabetesData: {
                totalPeriodDays: '',
                bloodTestCount: '',
                insulinCount: '',
            },
            oxygenData: {
                diagnosisDate: '', // 산소치료 진단일
                isHomeUnit: true,  // 가정용 여부 (true: 가정용, false: 휴대용)
                benefitStartDate: '', // 급여 적용일
                currentDate: '',   // 계산 종료일 (기본값: 오늘)
            },
            assistanceData: {
                claimType: 'general', // 청구 타입 (general: 일반, basic: 기초)
                disabilityType: '', // 장애유형
                type: '', // 보조기기 유형
                standardAmount: '', // 기준액
                announcedAmount: '', // 고시금액
                purchasePrice: '', // 실구입금액
            },
            // 단가 정보
            unitPrices: {
                bloodTest: 500,  // 혈당검사 단가
                insulin: 800     // 인슐린주사 단가
            },
            quantityAdjustments: {}, // 수량 조절을 위한 상태 관리
            assistanceSearchKeyword: '',
        };
    },

    computed: {
        // 선택된 제품의 총 가격 (고시금액으로 사용)
        selectedProductPrice() {
            if (!this.selectedProducts || this.selectedProducts.length === 0) {
                return 0;
            }

            return this.selectedProducts.reduce((total, product) => {
                // quantityAdjustments에서 현재 수량을 가져오고, 없으면 기본 수량 사용
                const currentQuantity = this.quantityAdjustments[product.id] ?? product.quantity ?? 1;
                const price = product.price || 0;
                const productTotal = price * currentQuantity;

                return total + productTotal;
            }, 0);
        },

        // 필터링된 보조기기 목록 (검색용)
        filteredAssistanceGuideData() {
            if (!this.assistanceSearchKeyword) {
                return this.assistanceGuideData;
            }

            const keyword = this.assistanceSearchKeyword.toLowerCase();
            return this.assistanceGuideData.filter(item =>
                item.disabilityType.toLowerCase().includes(keyword) ||
                item.itemName.toLowerCase().includes(keyword)
            );
        },

        // 필터링된 보조기기 목록 (선택용)
        filteredAssistanceItems() {
            if (!this.assistanceData.disabilityType) {
                return [];
            }
            return this.assistanceGuideData.filter(item =>
                item.disabilityType === this.assistanceData.disabilityType
            );
        },

        // 보조기기 계산 결과
        assistanceCalculationResult() {
            if (!this.isValidAssistanceCalculation) {
                return null;
            }

            const claimType = this.assistanceData.claimType;
            const standardAmount = this.parseNumber(this.assistanceData.standardAmount);
            // 고시금액: 사용자가 입력한 값이 있으면 우선 사용, 없으면 selectedProducts의 총 가격 사용
            const userInputAmount = this.parseNumber(this.assistanceData.announcedAmount);
            const announcedAmount = userInputAmount > 0 ? userInputAmount : this.selectedProductPrice;

            if (standardAmount <= 0 || announcedAmount <= 0) {
                return null;
            }

            // 실구입금액 = 고시금액 (제품별 결정가격과 동일)
            const purchaseAmount = announcedAmount;

            // 지급기준금액 = 기준액, 고시금액, 실구입금액 중 최저금액
            const paymentStandardAmount = Math.min(standardAmount, announcedAmount, purchaseAmount);

            // 지원액 계산
            let supportAmount;
            if (claimType === 'basic') {
                // 기초급여/차상위: 지급기준금액의 100%
                supportAmount = paymentStandardAmount;
            } else {
                // 일반급여: 지급기준금액의 90%
                supportAmount = Math.round(paymentStandardAmount * 0.9);
            }

            // 본인부담금 = 실구입금액 - 지원액
            const patientAmount = Math.max(0, purchaseAmount - supportAmount);

            // 청구금액 = 지원액
            const claimAmount = supportAmount;

            const result = {
                standardAmount,
                announcedAmount,
                purchaseAmount,
                paymentStandardAmount,
                supportAmount,
                patientAmount,
                claimAmount,
                claimType
            };

            return result;
        },

        // 보조기기 계산 유효성 검사
        isValidAssistanceCalculation() {
            const userInputAmount = this.parseNumber(this.assistanceData.announcedAmount);
            const announcedAmount = userInputAmount > 0 ? userInputAmount : this.selectedProductPrice;

            const isValid = this.assistanceData.claimType &&
                this.assistanceData.disabilityType &&
                this.assistanceData.type &&
                this.parseNumber(this.assistanceData.standardAmount) > 0 &&
                announcedAmount > 0;

            return isValid;
        },

        // 보조기기 유형명
        assistanceTypeName() {
            const typeNames = {
                arm: '팔의지',
                leg: '다리의지',
                arm_support: '팔보조기',
                spine: '척추보조기',
                pelvis: '골반보조기',
                leg_support: '다리보조기',
                shoes: '교정용 신발류',
                other: '그 밖의 보조기기'
            };
            return typeNames[this.assistanceData.type] || '';
        },

        // 공단 고시 보조기기 데이터
        assistanceGuideData() {
            return [
                // 팔의지
                { id: 1, disabilityType: '팔의지', itemName: '어깨가슴 의지-미관형', supportAmount: 930000 },
                { id: 2, disabilityType: '팔의지', itemName: '어깨가슴 의지-기능형', supportAmount: 1530000 },
                { id: 3, disabilityType: '팔의지', itemName: '어깨관절 의지-미관형', supportAmount: 1020000 },
                { id: 4, disabilityType: '팔의지', itemName: '어깨관절 의지-기능형', supportAmount: 1530000 },
                { id: 5, disabilityType: '팔의지', itemName: '위팔 의지-미관형', supportAmount: 740000 },
                { id: 6, disabilityType: '팔의지', itemName: '위팔 의지-기능형', supportAmount: 1490000 },
                { id: 7, disabilityType: '팔의지', itemName: '팔꿈치관절 의지-미관형', supportAmount: 720000 },
                { id: 8, disabilityType: '팔의지', itemName: '팔꿈치관절 의지-기능형', supportAmount: 1640000 },
                { id: 9, disabilityType: '팔의지', itemName: '아래팔 의지-미관형', supportAmount: 650000 },
                { id: 10, disabilityType: '팔의지', itemName: '아래팔 의지-기능형', supportAmount: 980000 },
                { id: 11, disabilityType: '팔의지', itemName: '손목관절 의지-미관형', supportAmount: 520000 },
                { id: 12, disabilityType: '팔의지', itemName: '손목관절 의지-기능형', supportAmount: 830000 },
                { id: 13, disabilityType: '팔의지', itemName: '손 의지-미관형', supportAmount: 330000 },
                { id: 14, disabilityType: '팔의지', itemName: '손 의지-기능형', supportAmount: 610000 },
                { id: 15, disabilityType: '팔의지', itemName: '손가락의지-미관형', supportAmount: 150000 },

                // 다리의지
                { id: 16, disabilityType: '다리의지', itemName: '골반 의지', supportAmount: 2260000 },
                { id: 17, disabilityType: '다리의지', itemName: '엉덩이관절 의지', supportAmount: 2260000 },
                { id: 18, disabilityType: '다리의지', itemName: '넓적다리 의지-일반형', supportAmount: 1950000 },
                { id: 19, disabilityType: '다리의지', itemName: '넓적다리 의지-실리콘형', supportAmount: 2960000 },
                { id: 20, disabilityType: '다리의지', itemName: '무릎관절 의지-일반형', supportAmount: 1930000 },
                { id: 21, disabilityType: '다리의지', itemName: '무릎관절 의지-실리콘형', supportAmount: 2610000 },
                { id: 22, disabilityType: '다리의지', itemName: '종아리 의지-일반형', supportAmount: 1380000 },
                { id: 23, disabilityType: '다리의지', itemName: '종아리 의지-실리콘형', supportAmount: 2230000 },
                { id: 24, disabilityType: '다리의지', itemName: '발목 의지-일반형', supportAmount: 680000 },
                { id: 25, disabilityType: '다리의지', itemName: '발목 의지-실리콘형', supportAmount: 1440000 },
                { id: 26, disabilityType: '다리의지', itemName: '발 의지-일반형', supportAmount: 280000 },
                { id: 27, disabilityType: '다리의지', itemName: '발 의지-실리콘형', supportAmount: 790000 },

                // 팔보조기
                { id: 28, disabilityType: '팔보조기', itemName: '어깨 보조기', supportAmount: 290000 },
                { id: 29, disabilityType: '팔보조기', itemName: '팔꿈치-손목-손 보조기-일반형', supportAmount: 240000 },
                { id: 30, disabilityType: '팔보조기', itemName: '팔꿈치-손목-손 보조기-각도조절형', supportAmount: 270000 },
                { id: 31, disabilityType: '팔보조기', itemName: '손목-손 보조기', supportAmount: 110000 },
                { id: 32, disabilityType: '팔보조기', itemName: '손가락 보조기', supportAmount: 60000 },

                // 척추보조기
                { id: 33, disabilityType: '척추보조기', itemName: '목 보조기-필라델피아', supportAmount: 90000 },
                { id: 34, disabilityType: '척추보조기', itemName: '목 보조기-토마스소프트칼라', supportAmount: 70000 },
                { id: 35, disabilityType: '척추보조기', itemName: '목 보조기-자켓형', supportAmount: 380000 },
                { id: 36, disabilityType: '척추보조기', itemName: '등-허리 보조기-나이트 테일러식', supportAmount: 190000 },
                { id: 37, disabilityType: '척추보조기', itemName: '허리-엉치 보조기-윌리엄식', supportAmount: 200000 },
                { id: 38, disabilityType: '척추보조기', itemName: '등-허리-엉치 보조기-TLSO식 재킷', supportAmount: 460000 },
                { id: 39, disabilityType: '척추보조기', itemName: '허리 보조기-코르셋', supportAmount: 100000 },

                // 골반보조기
                { id: 40, disabilityType: '골반보조기', itemName: '골반 보조기', supportAmount: 150000 },

                // 다리보조기
                { id: 41, disabilityType: '다리보조기', itemName: '엉덩-무릎-발목-발 보조기-한쪽', supportAmount: 620000 },
                { id: 42, disabilityType: '다리보조기', itemName: '엉덩-무릎-발목-발 보조기-양쪽', supportAmount: 1020000 },
                { id: 43, disabilityType: '다리보조기', itemName: '무릎-발목-발 보조기', supportAmount: 530000 },
                { id: 44, disabilityType: '다리보조기', itemName: '무릎 보조기-관절운동 제한장치 부착형', supportAmount: 210000 },
                { id: 45, disabilityType: '다리보조기', itemName: '무릎 보조기-레녹스힐', supportAmount: 190000 },
                { id: 46, disabilityType: '다리보조기', itemName: '무릎 보조기-인대손상용', supportAmount: 90000 },
                { id: 47, disabilityType: '다리보조기', itemName: '발목-발 보조기-체중부하식', supportAmount: 410000 },
                { id: 48, disabilityType: '다리보조기', itemName: '발목-발 보조기-일체형(플라스틱)', supportAmount: 190000 },
                { id: 49, disabilityType: '다리보조기', itemName: '발목-발 보조기-관절형(플라스틱)', supportAmount: 310000 },
                { id: 50, disabilityType: '다리보조기', itemName: '발목-발 보조기-관절형(금속형)', supportAmount: 330000 },
                { id: 51, disabilityType: '다리보조기', itemName: '발목-발 보조기-크렌자크식(플라스틱)', supportAmount: 360000 },
                { id: 52, disabilityType: '다리보조기', itemName: '발목-발 보조기-크렌자크식(금속형)', supportAmount: 370000 },
                { id: 53, disabilityType: '다리보조기', itemName: '발 보조기-양쪽', supportAmount: 200000 },

                // 교정용 신발류
                { id: 54, disabilityType: '교정용 신발류', itemName: '맞춤형 교정용 신발-18세 이하/19세 이상', supportAmount: 250000 },

                // 그 밖의 보조기기
                { id: 55, disabilityType: '그 밖의 보조기기', itemName: '수동휠체어-일반형', supportAmount: 480000 },
                { id: 56, disabilityType: '그 밖의 보조기기', itemName: '수동휠체어-활동형', supportAmount: 1000000 },
                { id: 57, disabilityType: '그 밖의 보조기기', itemName: '수동휠체어-틸팅형', supportAmount: 800000 },
                { id: 58, disabilityType: '그 밖의 보조기기', itemName: '지팡이', supportAmount: 20000 },
                { id: 59, disabilityType: '그 밖의 보조기기', itemName: '목발', supportAmount: 15000 },
                { id: 60, disabilityType: '그 밖의 보조기기', itemName: '의안', supportAmount: 620000 },
                { id: 61, disabilityType: '그 밖의 보조기기', itemName: '저시력 보조안경', supportAmount: 100000 },
                { id: 62, disabilityType: '그 밖의 보조기기', itemName: '콘택트렌즈', supportAmount: 80000 },
                { id: 63, disabilityType: '그 밖의 보조기기', itemName: '돋보기', supportAmount: 100000 },
                { id: 64, disabilityType: '그 밖의 보조기기', itemName: '망원경', supportAmount: 100000 },
                { id: 65, disabilityType: '그 밖의 보조기기', itemName: '흰지팡이', supportAmount: 25000 },
                { id: 66, disabilityType: '그 밖의 보조기기', itemName: '보청기', supportAmount: 1310000 },
                { id: 67, disabilityType: '그 밖의 보조기기', itemName: '개인용 음성증폭기', supportAmount: 500000 },
                { id: 68, disabilityType: '그 밖의 보조기기', itemName: '전동휠체어-가군', supportAmount: 2360000 },
                { id: 69, disabilityType: '그 밖의 보조기기', itemName: '의료용 스쿠터', supportAmount: 1920000 },
                { id: 70, disabilityType: '그 밖의 보조기기', itemName: '자세보조용구-앉기형-몸통 및 골반지지대', supportAmount: 880000 },
                { id: 71, disabilityType: '그 밖의 보조기기', itemName: '자세보조용구-앉기형-머리 및 목지지대', supportAmount: 210000 },
                { id: 72, disabilityType: '그 밖의 보조기기', itemName: '자세보조용구-앉기형-팔 지지대 및 랩트레이', supportAmount: 170000 },
                { id: 73, disabilityType: '그 밖의 보조기기', itemName: '자세보조용구-앉기형-다리 및 발 지지대', supportAmount: 240000 },
                { id: 74, disabilityType: '그 밖의 보조기기', itemName: '욕창예방방석', supportAmount: 250000 },
                { id: 75, disabilityType: '그 밖의 보조기기', itemName: '이동식전동리프트', supportAmount: 2500000 },
                { id: 76, disabilityType: '그 밖의 보조기기', itemName: '보행차-전방', supportAmount: 50000 },
                { id: 77, disabilityType: '그 밖의 보조기기', itemName: '보행차-후방', supportAmount: 300000 },
                { id: 78, disabilityType: '그 밖의 보조기기', itemName: '욕창예방매트리스', supportAmount: 400000 },
                { id: 79, disabilityType: '그 밖의 보조기기', itemName: '전동휠체어-나군', supportAmount: 3800000 },
                { id: 80, disabilityType: '그 밖의 보조기기', itemName: '수동휠체어-리클라이닝형', supportAmount: 800000 },
                { id: 81, disabilityType: '그 밖의 보조기기', itemName: '자세보조용구-서기형-기립훈련기', supportAmount: 2200000 }
            ];
        },

        // 산소발생기 계산 결과 (자동 계산)
        oxygenCalculationResult() {
            if (!this.isValidOxygenCalculation) {
                return null;
            }

            const DAY = 1000 * 60 * 60 * 24;
            const diagnosis = new Date(this.oxygenData.diagnosisDate);
            const today = new Date(this.oxygenData.currentDate || new Date());

            // 급여 적용일: 사용자가 입력한 값 또는 자동 계산된 값 사용
            const benefitStart = this.oxygenData.benefitStartDate
                ? new Date(this.oxygenData.benefitStartDate)
                : new Date(diagnosis.getTime() + 90 * DAY);

            // 요율 정의
            const feeTable = {
                home: {
                    nonBenefit: 4000, // 비급여 (가정용)
                    benefit: 400      // 급여 (가정용 10%)
                },
                portable: {
                    nonBenefit: 6667, // 비급여 (휴대용)
                    benefit: 667      // 급여 (휴대용 10%)
                }
            };

            const fee = this.oxygenData.isHomeUnit ? feeTable.home : feeTable.portable;
            const ranges = [];

            // 1. 비급여 기간: 진단일 ~ 급여 시작 전날
            if (benefitStart > diagnosis) {
                const nonBenefitEnd = new Date(benefitStart.getTime() - DAY);
                const endDate = today < benefitStart ? today : nonBenefitEnd;

                if (diagnosis <= endDate) {
                    const days = Math.floor((endDate - diagnosis) / DAY) + 1;
                    ranges.push({
                        type: "비급여",
                        startDate: new Date(diagnosis),
                        endDate: new Date(endDate),
                        days,
                        dailyRate: fee.nonBenefit,
                        amount: days * fee.nonBenefit
                    });
                }
            }

            // 2. 급여 기간: 급여 적용일 ~ 계산 종료일
            if (today >= benefitStart) {
                const benefitEndDate = today;
                const days = Math.floor((benefitEndDate - benefitStart) / DAY) + 1;

                if (days > 0) {
                    ranges.push({
                        type: "급여",
                        startDate: new Date(benefitStart),
                        endDate: new Date(benefitEndDate),
                        days,
                        dailyRate: fee.benefit,
                        amount: days * fee.benefit
                    });
                }
            }

            // 총계 계산
            const totalDays = ranges.reduce((sum, range) => sum + range.days, 0);
            const totalAmount = ranges.reduce((sum, range) => sum + range.amount, 0);
            const nonInsuranceDays = ranges.find(r => r.type === "비급여")?.days || 0;
            const insuranceDays = ranges.find(r => r.type === "급여")?.days || 0;
            const nonInsuranceAmount = ranges.find(r => r.type === "비급여")?.amount || 0;
            const insuranceAmount = ranges.find(r => r.type === "급여")?.amount || 0;

            return {
                diagnosisDate: this.oxygenData.diagnosisDate,
                benefitStartDate: benefitStart,
                currentDate: today,
                isHomeUnit: this.oxygenData.isHomeUnit,
                totalDays,
                insuranceDays,
                nonInsuranceDays,
                insuranceAmount,
                nonInsuranceAmount,
                totalAmount,
                detailBreakdown: ranges
            };
        },

        // 산소발생기 계산 유효성 검사
        isValidOxygenCalculation() {
            return this.oxygenData.diagnosisDate &&
                (this.oxygenData.currentDate || true); // currentDate가 없으면 오늘 날짜 사용
        },

        // 수량이 조절된 품목 목록
        selectedProductsWithQuantity() {
            return this.selectedProducts.map(p => ({
                ...p,
                currentQuantity: this.quantityAdjustments[p.id] ?? p.quantity ?? 0
            }));
        },

        diabetesSummary() {
            const totalDays = this.parseNumber(this.diabetesData.totalPeriodDays);
            const insulin = this.parseNumber(this.diabetesData.insulinCount);
            if (totalDays <= 0 || insulin <= 0) return null;

            // ✅ 일 기준금액 결정: 1회=900, 2회=1800, 3회 이상=2500
            const dailyCap = insulin >= 3 ? 2500 : (insulin === 2 ? 1800 : 900);
            const capTotal = dailyCap * totalDays;
            return { dailyCap, totalDays, capTotal };
        },

        // 핵심 부분만 발췌: diabetesCalculatedItems()
        diabetesCalculatedItems() {
            if (!this.diabetesSummary || this.selectedProductsWithQuantity.length === 0) return [];

            // 1) 품목별 실구입가
            const itemsBase = this.selectedProductsWithQuantity.map(p => {
                const isBox = p.type === '박스';
                const qtyDesc = isBox
                    ? `${p.currentQuantity}박스 × ${this.formatPrice(p.price || 0)}원`
                    : `${p.currentQuantity}개 × ${this.formatPrice(p.price || 0)}원`;
                const itemTotal = (p.price || 0) * (p.currentQuantity || 0); // 박스가×박스수 / 개당가×개수
                return { name: p.name, qtyDesc, itemTotal };
            });

            const totalPurchase = itemsBase.reduce((s, it) => s + it.itemTotal, 0);
            const capTotal = this.diabetesSummary.capTotal;
            const effectiveCapTotal = Math.min(capTotal, totalPurchase);
            if (effectiveCapTotal === 0) return [];

            const lastIndex = itemsBase.length - 1;

            // 2) 기준내 배정액: 비례배분(모든 행 floor) + 마지막 행에서 잔여 보정
            let runningAllocated = 0;
            const allocated = itemsBase.map((it, idx) => {
                let inCapAllocated;
                if (effectiveCapTotal === totalPurchase) {
                    inCapAllocated = it.itemTotal;              // cap이 총구입가 이상 → 전액 기준내
                } else {
                    inCapAllocated = (idx === lastIndex)
                        ? (effectiveCapTotal - runningAllocated)  // 마지막 행 보정
                        : Math.floor(it.itemTotal * effectiveCapTotal / totalPurchase);
                    runningAllocated += (idx === lastIndex ? 0 : inCapAllocated);
                }
                return { ...it, inCapAllocated };
            });
            // 누적 갱신(마지막 행 포함)
            runningAllocated = allocated.reduce((s, it) => s + it.inCapAllocated, 0);
            // 안전장치: 혹시 1원 오차 있으면 마지막 행에 재보정
            const allocDiff = effectiveCapTotal - runningAllocated;
            if (allocDiff !== 0) allocated[lastIndex].inCapAllocated += allocDiff;

            // 3) 공단 90%: 각 행은 round로 계산하되, 마지막 행에서 총합 강제 일치
            const targetInsurerTotal = Math.round(effectiveCapTotal * 0.9);
            let runningInsurer = 0;

            const items = allocated.map((it, idx) => {
                let insurer = (idx === lastIndex)
                    ? (targetInsurerTotal - runningInsurer)
                    : Math.round(it.inCapAllocated * 0.9);
                // 경계보정
                insurer = Math.max(0, Math.min(insurer, it.inCapAllocated));

                const patientInCap = it.inCapAllocated - insurer;      // 기준내 10%
                const overCap = Math.max(0, it.itemTotal - it.inCapAllocated); // 기준초과 100%
                const patientTotal = patientInCap + overCap;

                if (idx !== lastIndex) runningInsurer += insurer;

                return { ...it, insurer, patientInCap, overCap, patientTotal };
            });

            return items;
        },

        diabetesTotals() {
            const items = this.diabetesCalculatedItems;
            if (items.length === 0) {
                return { totalPurchase: 0, inCapTotal: 0, totalInsurer: 0, totalPatientInCap: 0, totalOverCap: 0, totalPatient: 0 };
            }
            const totalPurchase = items.reduce((s, it) => s + it.itemTotal, 0);
            const inCapTotal = items.reduce((s, it) => s + it.inCapAllocated, 0); // ✅ 추가
            const totalInsurer = items.reduce((s, it) => s + it.insurer, 0);
            const totalPatientInCap = items.reduce((s, it) => s + it.patientInCap, 0);
            const totalOverCap = items.reduce((s, it) => s + it.overCap, 0);
            const totalPatient = items.reduce((s, it) => s + it.patientTotal, 0);
            return { totalPurchase, inCapTotal, totalInsurer, totalPatientInCap, totalOverCap, totalPatient };
        },

        // 계산된 품목 목록
        calculatedItems() {
            if (!this.diabetesData.totalPeriodMonths || !this.selectedProducts.length) {
                return [];
            }

            const totalPeriod = this.parseNumber(this.diabetesData.totalPeriodMonths);
            const bloodTestCount = this.parseNumber(this.diabetesData.bloodTestCount);
            const insulinCount = this.parseNumber(this.diabetesData.insulinCount);

            const items = [];

            // 전달받은 품목의 개수만큼 항목 생성
            this.selectedProductsWithQuantity.forEach(product => {
                // 청구 수량 계산: 품목 분류가 인슐린이면 총 처방기간 × 인슐린주사 횟수, 그 외에는 총 처방기간 × 혈당검사 횟수
                let claimQuantity;
                if (product.category === '인슐린' || product.name.includes('주사바늘') || product.name.includes('인슐린')) {
                    claimQuantity = totalPeriod * insulinCount;
                } else {
                    claimQuantity = totalPeriod * bloodTestCount;
                }

                // 조절된 수량 사용
                const currentQuantity = product.currentQuantity;

                // 포장 수량: 박스일 경우 박스당 개수 × 조절된 품목 수량으로 표시, 그 외에는 조절된 품목 개수만 표시
                let packageInfo;
                let providedQuantity;
                if (product.type === '박스') {
                    const piecesPerBox = product.pieces_per_box || 1;
                    packageInfo = `${piecesPerBox}개/박스 ×${currentQuantity}박스`;
                    providedQuantity = piecesPerBox * currentQuantity;
                } else {
                    packageInfo = `${currentQuantity}개`;
                    providedQuantity = currentQuantity;
                }

                // 단가: 박스일 경우 품목 단가 / 박스당 개수, 그 외에는 품목 단가 / 조절된 품목 수량
                let unitPrice;
                if (product.type === '박스') {
                    const piecesPerBox = product.pieces_per_box || 1;
                    unitPrice = Math.round((product.price || 0) / piecesPerBox);
                } else {
                    unitPrice = Math.round((product.price || 0) / currentQuantity);
                }

                // 청구 금액 계산: 박스일 경우 기존 방식, 그 외에는 포장 수량 × 단가
                let claimAmount;
                if (product.type === '박스') {
                    claimAmount = claimQuantity * unitPrice;
                } else {
                    claimAmount = providedQuantity * unitPrice;
                }

                // 초과 수량: 제공 수량 - 청구 수량
                const excessQuantity = Math.max(0, providedQuantity - claimQuantity);

                // 자비부담 금액: 초과수량 × 단가
                const selfPayAmount = excessQuantity * unitPrice;

                items.push({
                    name: product.name,
                    claimQuantity,
                    packageInfo,
                    providedQuantity,
                    unitPrice,
                    claimAmount,
                    excessQuantity,
                    selfPayAmount,
                    unit: '개'
                });
            });

            return items;
        },

        // 총 청구 금액
        totalClaimAmount() {
            return this.calculatedItems.reduce((sum, item) => sum + item.claimAmount, 0);
        },

        // 총 자비부담 금액
        totalSelfPayAmount() {
            return this.calculatedItems.reduce((sum, item) => sum + item.selfPayAmount, 0);
        }
    },

    methods: {
        getInitialTab() {
            // orderType prop에 따라 초기 탭 결정
            switch (this.orderType) {
                case 'oxygen':
                    return 'oxygen';
                case 'diabetes':
                    return 'diabetes';
                case 'assistance':
                    return 'assistance';
                default:
                    return 'diabetes';
            }
        },

        setActiveTab(tab) {
            this.activeTab = tab;
        },

        // 보조기기 검색 처리
        onAssistanceSearch() {
            // 검색 키워드가 변경될 때마다 자동으로 필터링됨
            console.log('보조기기 검색:', this.assistanceSearchKeyword);
        },

        // 보조기기 아이템 선택
        selectAssistanceItem(item) {
            // 장애유형과 보조기기 유형 자동 설정
            this.assistanceData.disabilityType = item.disabilityType;
            this.assistanceData.type = item.itemName;
            this.assistanceData.standardAmount = item.supportAmount.toString();
            // 고시금액은 selectedProducts의 가격을 사용하므로 수동 입력 필드는 비워둠
            this.assistanceData.announcedAmount = '';

            // 검색 키워드 초기화
            this.assistanceSearchKeyword = '';
        },
        // 아이템 하이라이트 여부 확인
        isItemHighlighted(item) {
            return this.assistanceData.type === item.itemName;
        },

        // 보조기기 숫자 입력 처리 (콤마 포함)
        handleAssistanceNumberInput(field, value) {
            const cleanValue = String(value).replace(/[^0-9]/g, '');

            if (field === 'assistanceData.standardAmount') {
                this.assistanceData.standardAmount = cleanValue;
            } else if (field === 'assistanceData.announcedAmount') {
                this.assistanceData.announcedAmount = cleanValue;
            }
        },

        // 숫자 포맷팅 (콤마 추가)
        formatNumber(value) {
            if (!value || value === '') return '';
            const num = parseInt(String(value).replace(/[^0-9]/g, ''), 10);
            return isNaN(num) ? '' : new Intl.NumberFormat('ko-KR').format(num);
        },

        // 장애유형 변경 시 보조기기 유형 초기화
        onDisabilityTypeChange() {
            this.assistanceData.type = '';
            this.assistanceData.standardAmount = '';
        },

        // 보조기기 유형 변경 시 기준액 자동 설정
        onAssistanceTypeChange() {
            if (this.assistanceData.type) {
                const selectedItem = this.assistanceGuideData.find(item =>
                    item.itemName === this.assistanceData.type
                );
                if (selectedItem) {
                    this.assistanceData.standardAmount = selectedItem.supportAmount.toString();
                    // 고시금액은 selectedProducts의 가격을 사용하므로 수동 입력 필드는 비워둠
                    this.assistanceData.announcedAmount = '';
                }
            }
        },

        // 보조기기 데이터 입력 처리
        handleAssistanceInput(field, value) {
            if (field === 'assistanceData.disabilityType') {
                this.assistanceData.disabilityType = value;
            } else if (field === 'assistanceData.type') {
                this.assistanceData.type = value;
            } else if (field === 'assistanceData.standardAmount') {
                const clean = String(value).replace(/[^0-9]/g, '');
                this.assistanceData.standardAmount = clean;
            } else if (field === 'assistanceData.announcedAmount') {
                const clean = String(value).replace(/[^0-9]/g, '');
                this.assistanceData.announcedAmount = clean;
            } else if (field === 'assistanceData.purchasePrice') {
                const clean = String(value).replace(/[^0-9]/g, '');
                this.assistanceData.purchasePrice = clean;
            }
        },

        // 숫자 입력 처리 - 보조기기용으로 확장
        handleNumberInput(field, value) {
            if (field.startsWith('assistanceData.')) {
                this.handleAssistanceInput(field, value);
            } else {
                const clean = String(value).replace(/[^0-9]/g, '');
                this.diabetesData[field] = clean;
            }
        },

        // 산소발생기 날짜 입력 처리
        handleOxygenDateInput(field, value) {
            this.oxygenData[field] = value;

            // 진단일이 변경되면 급여 적용일 자동 계산
            if (field === 'diagnosisDate' && value) {
                this.calculateBenefitStartDate(value);
            }
        },

        // 급여 적용일 자동 계산 (진단일 + 90일)
        calculateBenefitStartDate(diagnosisDate) {
            if (!diagnosisDate) return;

            const diagnosis = new Date(diagnosisDate);
            const benefitStart = new Date(diagnosis);
            benefitStart.setDate(benefitStart.getDate() + 90);

            // YYYY-MM-DD 형식으로 변환
            const year = benefitStart.getFullYear();
            const month = String(benefitStart.getMonth() + 1).padStart(2, '0');
            const day = String(benefitStart.getDate()).padStart(2, '0');

            this.oxygenData.benefitStartDate = `${year}-${month}-${day}`;
        },

        // 산소발생기 장비 타입 변경
        handleOxygenTypeChange(isHome) {
            this.oxygenData.isHomeUnit = isHome;
        },

        closeModal() {
            this.$emit('close');
        },

        setActiveTab(tab) {
            this.activeTab = tab;
        },

        formatDate(date) {
            if (!date) return '';
            const d = new Date(date);
            return d.toLocaleDateString('ko-KR');
        },

        // 문자열을 숫자로 파싱 (공백 제거 후)
        parseNumber(v) {
            const n = parseInt(String(v || '').replace(/\s/g, ''), 10);
            return isNaN(n) ? 0 : n;
        },

        increaseQuantity(id) {
            const cur = this.quantityAdjustments[id] ??
                this.selectedProducts.find(p => p.id === id)?.quantity ?? 0;
            if (cur < 999) this.quantityAdjustments[id] = cur + 1;
        },
        decreaseQuantity(id) {
            const cur = this.quantityAdjustments[id] ??
                this.selectedProducts.find(p => p.id === id)?.quantity ?? 0;
            if (cur > 1) this.quantityAdjustments[id] = cur - 1;
        },
        updateQuantity(id, value) {
            const v = parseInt(value) || 1;
            this.quantityAdjustments[id] = Math.max(1, Math.min(999, v));
        },

        // 바코드 스캐너 제어 메서드
        disableBarcodeScanner() {
            // 부모 컴포넌트의 바코드 스캐너 비활성화 메서드 호출
            if (this.$parent && typeof this.$parent.disableBarcodeScanner === 'function') {
                this.$parent.disableBarcodeScanner();
            }
        },

        enableBarcodeScanner() {
            // 부모 컴포넌트의 바코드 스캐너 활성화 메서드 호출
            if (this.$parent && typeof this.$parent.enableBarcodeScanner === 'function') {
                this.$parent.enableBarcodeScanner();
            }
        },

        // 혈당검사 총 사용량 계산
        calculateTotalBloodTest() {
            const bloodTestCount = this.parseNumber(this.diabetesData.bloodTestCount);
            const totalPeriod = this.parseNumber(this.diabetesData.totalPeriodMonths);
            return bloodTestCount * totalPeriod;
        },

        // 인슐린주사 총 사용량 계산
        calculateTotalInsulin() {
            const insulinCount = this.parseNumber(this.diabetesData.insulinCount);
            const totalPeriod = this.parseNumber(this.diabetesData.totalPeriodMonths);
            return insulinCount * totalPeriod;
        },

        // 혈당검사 총 금액 계산
        calculateBloodTestAmount() {
            return this.calculateTotalBloodTest() * this.unitPrices.bloodTest;
        },

        // 인슐린주사 총 금액 계산
        calculateInsulinAmount() {
            return this.calculateTotalInsulin() * this.unitPrices.insulin;
        },

        // 전체 총 금액 계산
        calculateGrandTotal() {
            return this.calculateBloodTestAmount() + this.calculateInsulinAmount();
        },

        formatPrice(price) {
            return new Intl.NumberFormat('ko-KR').format(price || 0);
        },

        // 컴포넌트 내부에서 호출할 메서드들
        handleModalOpen() {
            this.disableBarcodeScanner();
        },

        handleModalClose() {
            this.enableBarcodeScanner();
        }
    },

    // 모달 생명주기 관리
    watch: {
        orderType: {
            handler(newType) {
                // orderType이 변경되면 해당 탭으로 이동
                this.activeTab = this.getInitialTab();
            },
            immediate: true
        },
        show: {
            handler(newValue) {
                if (newValue) {
                    // 모달이 열릴 때 바코드 스캐너 비활성화
                    this.$nextTick(() => {
                        this.disableBarcodeScanner();
                    });
                } else {
                    // 모달이 닫힐 때 바코드 스캐너 재활성화
                    this.$nextTick(() => {
                        this.enableBarcodeScanner();
                    });
                }
            },
            immediate: false
        }
    },

    mounted() {
        const today = new Date();
        this.oxygenData.currentDate = today.toISOString().split('T')[0];

        if (this.diabetesData.totalPeriodMonths && !this.diabetesData.totalPeriodDays) {
            this.diabetesData.totalPeriodDays = this.diabetesData.totalPeriodMonths;
        }

        if (this.show) {
            this.$nextTick(() => this.disableBarcodeScanner());
        }
    },

    beforeUnmount() {
        // 컴포넌트가 언마운트되기 전에 바코드 스캐너 재활성화
        this.enableBarcodeScanner();
    }
}
</script>